/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import {
  Box,
  Button,
  Text,
  Spacer,
  FlexContainer,
  theme,
} from "@nordcloud/gnui";
import { useUpdateDriftsMutation } from "~/generated/graphql";
import { Details } from "~/components";
import { Can, ACTIONS } from "~/services/auth";
import { isEmpty } from "~/tools";
import { AttachToEnv } from "./AttachToEnv";
import { EstateRecord } from "./EstateDetailsPage/types";
import { getResources } from "./helpers";

type Props = {
  refetch: () => void;
  estateRecord: EstateRecord;
};

export function Application({ refetch, estateRecord }: Props) {
  const [showAttachmentSidebar, setShowAttachmentSidebar] =
    React.useState(false);

  const [updateDrift] = useUpdateDriftsMutation();

  const onDriftUpdate = (ids: string[], accept: boolean) => {
    updateDrift({ variables: { ids, accept: !!accept } })
      .then(() => refetch())
      .catch(console.log);
  };

  const pendingApplicationDetails =
    estateRecord?.__typename === "CloudResource"
      ? [
          {
            label: "Application",
            value: estateRecord?.pendingAssignment?.application?.name || "-",
            target: `/applications/${estateRecord?.pendingAssignment?.application?.id}`,
          },
          {
            label: "Environment",
            value: estateRecord?.pendingAssignment?.name || "-",
            target: `/applications/${estateRecord?.pendingAssignment?.application?.id}/${estateRecord?.pendingAssignment?.id}`,
          },
        ]
      : [];

  const applicationDetails =
    estateRecord?.__typename === "CloudResource"
      ? [
          {
            label: "Application",
            value: estateRecord?.environment?.application?.name || "-",
            target: `/applications/${estateRecord?.environment?.application?.id}`,
          },
          {
            label: "Environment",
            value: estateRecord?.environment?.name || "-",
            target: `/applications/${estateRecord?.environment?.application?.id}/${estateRecord?.environment?.id}`,
          },
        ]
      : [];

  const isDriftedCloudResource =
    estateRecord?.__typename === "CloudResource" && estateRecord?.drifted;

  if (isEmpty(applicationDetails)) {
    return null;
  }

  return (
    <FlexContainer columnGap={theme.spacing.spacing02} alignItems="stretch">
      <Box>
        <Text weight="medium">Application</Text>
        <Details details={applicationDetails} />
        <Can action={ACTIONS.updateMapping}>
          <Spacer height={theme.spacing.spacing02} />
          <Button
            severity="medium"
            icon="link"
            size="md"
            onClick={() => setShowAttachmentSidebar(true)}
          >
            Attach to different environment
          </Button>
          <AttachToEnv
            allowMultipleAssignment
            resources={getResources(estateRecord)}
            refresh={refetch}
            isOpen={showAttachmentSidebar}
            onClose={() => setShowAttachmentSidebar(false)}
          />
        </Can>
      </Box>

      {isDriftedCloudResource && (
        <Box>
          <Text weight="medium" color="danger">
            Drift detected
          </Text>
          <FlexContainer direction="column" alignItems="start">
            <Details details={pendingApplicationDetails} />

            <Spacer height={theme.spacing.spacing02} />
            <FlexContainer>
              <Button
                mr={theme.spacing.spacing02}
                severity="medium"
                size="md"
                icon="success"
                onClick={() => onDriftUpdate([estateRecord?.id ?? ""], true)}
              >
                Approve
              </Button>
              <Button
                outline
                severity="medium"
                size="md"
                icon="cancel"
                onClick={() => onDriftUpdate([estateRecord?.id ?? ""], false)}
              >
                Reject
              </Button>
            </FlexContainer>
          </FlexContainer>
        </Box>
      )}
    </FlexContainer>
  );
}
